import { useQuery } from '@based/sdk/react'
import { useMemo } from 'react'
import { EventCard } from '@app/components/blocks/event/EventCard'
import { Text } from '@app/components/core/typography/Text'
import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import type { ListEventModel } from 'functions/lib/event/eventList'
import defaultProjectPicture from '@app/assets/defaultProjectPicture.png'
import { pages } from '@app/util/links'

export const Events = ({ initiativeId }: { initiativeId: number }) => {
  const translations = useTranslations().app
  const initiativeEventsQuery: { data?: ListEventModel[] } = useQuery(
    'list-initiative-events',
    {
      initiativeId,
      filterPastEvents: false,
    },
  )
  const events = initiativeEventsQuery.data

  const { upcomingEvents, pastEvents } = useMemo(() => {
    if (!events) {
      return { upcomingEvents: [], pastEvents: [] }
    }

    const now = Date.now()
    const startOfToday = new Date(now)
    startOfToday.setHours(0, 0, 0, 0)
    const isPastEvent = (event: ListEventModel) =>
      event.start < startOfToday.getTime() && event.end !== 0 && event.end < now

    return {
      upcomingEvents: events.filter((event) => !isPastEvent(event)),
      pastEvents: events.filter((event) => isPastEvent(event)),
    }
  }, [events])

  if (!events || events.length === 0) {
    return (
      <Column gap="1rem">
        <Text.Medium color="var(--palette-blue-600)">
          {translations.initiativePage.noEvents}
        </Text.Medium>
      </Column>
    )
  }

  const renderCard = (event: ListEventModel) => (
    <EventCard
      key={event.uuid}
      title={event.title}
      start={event.start}
      end={event.end}
      image={event.images?.[0]?.src ?? defaultProjectPicture}
      imageAlt={event.images?.[0]?.altText}
      to={pages?.event?.(event.slug)}
      users={event.users}
    />
  )

  return (
    <Column gap="1.5rem">
      {upcomingEvents.map(renderCard)}

      {pastEvents.length > 0 && (
        <>
          <Title.H3 style={{ marginTop: '2rem', marginBottom: 0 }}>
            {translations.eventsPage.pastEvents}
          </Title.H3>
          {pastEvents.map(renderCard)}
        </>
      )}
    </Column>
  )
}
